<template>
    <div :id="getTableId()" class="relative VideoTable">
        <div v-show="loading" class="absolute z-50 w-full h-full bg-spinner flex flex-col items-center">
            <spinner class="mb-1"></spinner>
        </div>

        <div class="border-t-2 border-blue-600">
            <div class="xs:flex-col table-header">
                <div class="w-full sm:w-auto pr-3 xs:pr-0 xs:pb-2" v-if="showTopics">
                    <label class="self-center align-middle text-black mr-1" for="category">Topic:</label>
                    <select class="form-control form-control-sm form-control-fixed" id="category" name="category" v-model="category">
                        <option value="">All Videos</option>
                        <option value="market review">Market Review</option>
                        <option value="strategies">Strategies</option>
                        <option value="indicators">Indicators</option>
                        <option value="chart basics">Chart Basics</option>
                    </select>
                </div>
                <div class="relative w-full sm:w-auto">
                    <label class="self-center align-middle text-black mr-1" for="perPage">Per Page:</label>
                    <select class="form-control form-control-sm form-control-fixed-sm" id="perPage" name="perPage" v-model.number="perPage">
                        <option value="5">5</option>
                        <option value="15">15</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <!-- <option :value="total">All</option> -->
                    </select>
                </div>
                <div class="relative w-full md:w-auto md:my-0 my-2 ml-auto">
                    <input class="form-input-inline form-input-sm-inline -mr-6" style="width:auto !important;" @keyup.enter="setFilter" placeholder="Enter a keyword..." v-model="searchFor" aria-label="Enter a keyword" />
                    <button class="inline-block search-clear" @click="resetFilter" aria-label="Reset"><i class="fas fa-times"></i></button>
                    <button class="inline-block btn btn-sm btn-yellow" @click="setFilter" aria-label="Search..."><i class="fas fa-search"></i></button>
                </div>
            </div>
        </div>

        <div class="table-responsive">
            <vuetable
                ref="vuetableRef"
                :css="{
                    tableClass: 'table table-bordered table-hover',
                    tableBodyClass: '',
                    loadingClass: 'Please wait...',
                    ascendingIcon: 'fas fa-sort-alpha-down',
                    descendingIcon: 'fas fa-sort-alpha-up',
                    handleIcon: 'fas fa-bars',
                    sortableIcon: 'fas fa-sort text-gray-500',
                    ascendingClass: 'sorted-asc',
                    descendingClass: 'sorted-desc',
                }"
                :api-url="apiUrl"
                :fields="fields"
                :sort-order="sortOrder"
                :per-page="perPage"
                track-by="video_id"
                :append-params="moreParams"
                pagination-path=""
                @vuetable:pagination-data="onPaginationData"
                @vuetable:loading="onLoading"
                @vuetable:loaded="onLoaded"
                @vuetable:load-success="updateTotal"
            >
                <template #title-slot="props">
                    <a href="" @click.prevent="onActionClicked(titleAction || 'preview', props.rowData)">
                        {{ props.rowData.title }}
                    </a>
                </template>
                <template #actions="props">
                    <button @click="onActionClicked('modal', props.rowData)" class="text-blue-600 pb-1 mb-1 lg:pb-0 lg:mb-0" title="View video in current window"><i class="fas fa-video px-1"></i></button>
                    <button @click="onActionClicked('external-open', props.rowData)" class="text-blue-600 pb-1 lg:pb-0" title="View video in new window"><i class="fas fa-external-link-alt px-1"></i></button>
                </template>
            </vuetable>
            <div class="mt-4 text-center">
                <vuetable-pagination
                    :css="{
                        icons: {
                            first: 'fas fa-angle-double-left',
                            prev: 'fas fa-angle-left',
                            next: 'fas fa-angle-right',
                            last: 'fas fa-angle-double-right'
                        }
                    }"
                    ref="paginationRef"
                    @vuetable-pagination:change-page="onChangePage"
                ></vuetable-pagination>

                <vuetable-pagination-info
                    class="mt-2"
                    ref="paginationInfoRef"
                    info-class="pagination-info"
                ></vuetable-pagination-info>
            </div>
        </div>

        <video-modal v-if="modalObj.show" @close="modalObj.show = false" :header="modalObj.header" :description="modalObj.description" :url="modalObj.url" :moreinfo="modalObj.moreinfo" autoplay="true" :modalvideo="true"></video-modal>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch, computed } from 'vue';

const props = defineProps({
    url: {
        type: String,
        default: '/training/category',
    },
    showTopics: {
        type: Boolean,
        default: false,
    },
    query: {
        type: Object,
        default: () => ({})
    },
    titleAction: {
        type: String,
        default: 'preview',
    },
    fromDate: {
        type: String,
        default: '',
    },
    hideDate: {
        type: Boolean,
        default: false,
    },
    sortDir: {
        type: String,
        default: 'desc',
    },
});

const vuetableRef = ref(null);
const paginationRef = ref(null);
const paginationInfoRef = ref(null);

const modalObj = ref({
    header: '',
    description: '',
    moreinfo: '',
    url: '',
    show: false,
});

const fields = ref([
    {
        name: 'date',
        title: 'Date',
        sortField: 'date',
        width: "8%",
        titleClass: "text-center " + (props.hideDate ? 'hidden' : ''),
        dataClass: "text-center " + (props.hideDate ? 'hidden' : ''),
    },
    {
        name: 'title-slot',
        title: 'Title',
        sortField: 'title',
        width: "28%",
        titleClass: "text-center",
        dataClass: "",
    },
    {
        name: 'description',
        title: 'Description',
        sortField: 'description',
        width: "45%",
        titleClass: "text-center",
        dataClass: "",
    },
    {
        name: 'duration',
        title: 'Duration',
        sortField: 'duration',
        width: "8%",
        titleClass: "text-center",
        dataClass: "text-right",
    },
    {
        name: 'actions',
        title: 'Actions',
        width: "7%",
        titleClass: "text-center",
        dataClass: "text-center",
    },
]);

const category = ref('');
const apiUrl = props.url;
const searchFor = ref('');
const loading = ref(true);
const limitDate = ref(props.fromDate);
const perPage = ref(25);
const total = ref(25);

const sortOrder = ref([
    {
        field: "date",
        direction: props.hideDate ? "asc" : props.sortDir,
    },
]);

watch([perPage, category], async () => {
    await nextTick();
    vuetableRef.value.refresh();
});

const moreParams = computed(() => ({
    filter: searchFor.value,
    category: category.value,
    from_date: limitDate.value,
    ...props.query,
}));

const getTableId = () => 'VideoTable-' + new Date().getTime();

const setFilter = async () => {
    await nextTick();
    vuetableRef.value.refresh();
};

const resetFilter = () => {
    searchFor.value = '';
    setFilter();
};

const onPaginationData = (tablePagination) => {
    paginationRef.value.setPaginationData(tablePagination);
    paginationInfoRef.value.setPaginationData(tablePagination);
};

const onChangePage = (page) => {
    vuetableRef.value.changePage(page);
};

const onActionClicked = (action, data) => {
    if (action === 'preview') {
        window.open(window.route('main.videos.show', { video: data.video_id }), '_blank');
    } else  if (action === 'external-open') {
        window.open(window.route('main.videos.show', { video: data.video_id }), '_blank');
    } else {
        const url = window.route('main.download', { url: data.s3Path });
        modalObj.value = {
            header: data.title,
            description: data.description,
            moreinfo: 'Date Posted: ' + data.date,
            show: true,
            url: url,
        };
    }
};

const updateTotal = (response) => {
    total.value = response.data.total;
};

const onLoading = () => {
    loading.value = true;
};

const onLoaded = () => {
    loading.value = false;
};

const refreshTable = () => {
    vuetableRef.value.refresh();
};

onMounted(() => {
    refreshTable();
});

const toDateFormat = (value) => {
    if (props.dateFormat === 'string') {
        let date = new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        return date;
    }
    return value;
};
</script>

<style lang="scss">
.VideoTable {
    .bg-spinner {
        background: -moz-linear-gradient(top, rgba(242, 221, 161 ,0.4) 0%, rgba(0,0,0,0) 100%) !important;
        background: -webkit-linear-gradient(top, rgba(242, 221, 161 ,0.4) 0%,rgba(0,0,0,0) 100%) !important;
        background: linear-gradient(to bottom, rgba(242, 221, 161 ,0.4) 0%,rgba(0,0,0,0) 100%) !important;
    }

    .spinner {
        .spinner-child {
            background-color: #d38653 !important;
        }
    }

    .vuetable {
        th {
            padding-top: .4rem !important;
            padding-bottom: .4rem !important;
            i {
                @apply leading-normal;
            }
        }

        th, td {
            padding-left: .6rem !important;
            padding-right: .6rem !important;
        }
    }
    .pagination {
        a {
            cursor: pointer;
            @apply px-2 py-1 ml-1 text-blue-600;

            &.active {
                @apply text-white bg-blue-600 rounded;
            }

            &:hover {
                @apply text-white bg-blue-400 rounded;
            }

            &.btn-nav.disabled {
                cursor: not-allowed;
                @apply text-gray-400;
            }
        }
    }
    .vuetable-pagination-info {
        @apply text-sm;
    }
}
</style>
